/*
  Frame compositing - window.PeletonFrames
  ----------------------------------------
  Config comes from <script id="pf-config"> (snippets/framed-poster-config.liquid), which is
  generated from Theme settings > Frames. Nothing frame-specific is hardcoded here.

  PUBLIC CONTRACT (other components - e.g. the Desenio-style frame selector - drive this):

    document.dispatchEvent(new CustomEvent('peleton:frame-change', {
      detail: {
        frame:  'oak' | 'black' | 'dark-oak' | 'none' | <any configured key>,   // preferred
        handle: 'sort-ramme',            // alternative: a frame PRODUCT handle (mapped to key)
        scope:  Element                  // optional: only update composites inside this element
      }
    }));

  Every composite marked [data-pf-switch] in scope switches; the swatch row (if present) syncs.
  `frame` wins over `handle`. Unknown keys are ignored. 'none' shows the plain poster.

    PeletonFrames.list                 -> [{key,label,swatch,overlay,productHandle}]
    PeletonFrames.current              -> currently selected key
    PeletonFrames.get(key)             -> frame definition or null
    PeletonFrames.keyForHandle(handle) -> key for a frame product handle, or null
    PeletonFrames.select(key)          -> dispatches 'peleton:frame-change' (use from UI)
    PeletonFrames.setFrame(root, key)  -> low-level: sets data-frame on [data-pf-switch] in root
    PeletonFrames.render(el, opts)     -> builds a composite inside `el` (JS surfaces:
                                          popup, art wall). opts = {src, ratio, frame, alt,
                                          fit:'contain'|'cover', sizes, srcset, fill}
    PeletonFrames.geometry(ratio,fill) -> {w,h} in % of the square canvas (fill = long side %)
    PeletonFrames.init(root)           -> wire swatch rows / sync composites added after load
*/
(function () {
  if (window.PeletonFrames) return;

  var cfgEl = document.getElementById('pf-config');
  var cfg = { frames: [], defaultFrame: '', unframedSizes: '', surfaces: {} };
  if (cfgEl) {
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      console.warn('[PeletonFrames] invalid pf-config', e);
    }
  }

  var frames = (cfg.frames || []).filter(function (f) {
    return f && f.key;
  });
  var byKey = {};
  frames.forEach(function (f) {
    byKey[f.key] = f;
  });

  var unframedSizes = String(cfg.unframedSizes || '')
    .split(',')
    .map(function (s) {
      return normSize(s);
    })
    .filter(Boolean);

  function normSize(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/cm/g, '')
      .replace(/\s+/g, '')
      .replace(/×/g, 'x');
  }

  function cssNum(name, fallback) {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }

  var defaultFrame = byKey[cfg.defaultFrame] ? cfg.defaultFrame : frames[0] ? frames[0].key : 'none';

  var api = {
    list: frames,
    current: defaultFrame,
    defaultFrame: defaultFrame,
    surfaces: cfg.surfaces || {},
    unframed: false,

    get: function (key) {
      return byKey[key] || null;
    },

    keyForHandle: function (handle) {
      if (!handle) return null;
      for (var i = 0; i < frames.length; i++) {
        var hs = String(frames[i].productHandle || '')
          .split(',')
          .map(function (h) {
            return h.trim();
          });
        if (hs.indexOf(handle) !== -1) return frames[i].key;
      }
      return null;
    },

    isUnframedSize: function (size) {
      var n = normSize(size);
      return !!n && unframedSizes.indexOf(n) !== -1;
    },

    select: function (key, scope) {
      document.dispatchEvent(
        new CustomEvent('peleton:frame-change', { detail: { frame: key, scope: scope || null } })
      );
    },

    setFrame: function (root, key) {
      if (key !== 'none' && !byKey[key]) return;
      var scope = root || document;
      var nodes = scope.querySelectorAll ? scope.querySelectorAll('[data-pf-switch]') : [];
      if (scope.matches && scope.matches('[data-pf-switch]')) nodes = [scope].concat([].slice.call(nodes));
      [].forEach.call(nodes, function (n) {
        n.setAttribute('data-frame', key);
      });
    },

    geometry: function (ratio, fill) {
      var scale = cssNum('--pf-scale', 55);
      var band = cssNum('--pf-band', 1.6);
      var b2 = band * 2;
      var maxLong = 80;
      var w, h;
      ratio = ratio > 0 ? ratio : 0.714;
      if (fill > 0) {
        // Long side = fill % of the canvas (used where the frame itself is the product, e.g. popup)
        if (ratio <= 1) {
          h = fill;
          w = (h - b2) * ratio + b2;
        } else {
          w = fill;
          h = (w - b2) / ratio + b2;
        }
        return { w: +w.toFixed(3), h: +h.toFixed(3) };
      }
      if (ratio <= 1) {
        w = scale;
        h = (w - b2) / ratio + b2;
        if (h > maxLong) {
          h = maxLong;
          w = (h - b2) * ratio + b2;
        }
      } else {
        h = scale;
        w = (h - b2) * ratio + b2;
        if (w > maxLong) {
          w = maxLong;
          h = (w - b2) / ratio + b2;
        }
      }
      return { w: +w.toFixed(3), h: +h.toFixed(3) };
    },

    render: function (el, opts) {
      if (!el || !opts || !opts.src) return null;
      var g = api.geometry(opts.ratio, opts.fill);
      var frame = opts.frame && (byKey[opts.frame] || opts.frame === 'none') ? opts.frame : api.current;
      var fig = document.createElement('figure');
      fig.className = 'media pf-figure ' + (opts.fit || 'contain') + ' ' + (opts.aspect || 'natural');
      var pf = document.createElement('div');
      pf.className = 'pf';
      pf.setAttribute('data-pf', '');
      pf.setAttribute('data-frame', frame);
      pf.setAttribute('data-fit', opts.fit || 'contain');
      if (opts.switchable) pf.setAttribute('data-pf-switch', '');
      pf.style.setProperty('--pf-w', g.w);
      pf.style.setProperty('--pf-h', g.h);
      var canvas = document.createElement('div');
      canvas.className = 'pf__canvas';
      var art = document.createElement('div');
      art.className = 'pf__art';
      var img = document.createElement('img');
      img.className = 'pf__poster';
      img.alt = opts.alt || '';
      img.decoding = 'async';
      if (opts.srcset) {
        img.srcset = opts.srcset;
        img.sizes = opts.sizes || '100vw';
      }
      img.src = opts.src;
      // Low-res placeholder behind the poster (see .pf__poster in framed-poster.css).
      try {
        var tiny = new URL(opts.src, location.href);
        if (/\/cdn\/shop\/|cdn\.shopify\.com/.test(tiny.href)) {
          tiny.searchParams.set('width', '24');
          img.style.backgroundImage = 'url("' + tiny.href + '")';
        }
      } catch (e) { /* placeholder is optional */ }
      var fr = document.createElement('span');
      fr.className = 'pf__frame';
      fr.setAttribute('aria-hidden', 'true');
      art.appendChild(img);
      art.appendChild(fr);
      canvas.appendChild(art);
      pf.appendChild(canvas);
      fig.appendChild(pf);
      el.innerHTML = '';
      el.appendChild(fig);
      return pf;
    },

    /**
     * Pick the plain (unframed) poster image from a product JSON (/products/x.js or {{ product | json }}).
     * Mirrors the Liquid rule in snippets/framed-poster.liquid:
     *   poster-only filename/alt -> last image (unless it is a baked render / _peleton_ mockup).
     * Returns {src, ratio, alt} or null.
     */
    pickPoster: function (product) {
      if (!product) return null;
      var media = (product.media || []).filter(function (m) {
        return !m.media_type || m.media_type === 'image';
      });
      var list = media.length
        ? media.map(function (m) {
            return { src: m.src || (m.preview_image && m.preview_image.src), w: m.width || (m.preview_image && m.preview_image.width), h: m.height || (m.preview_image && m.preview_image.height), alt: m.alt || '' };
          })
        : (product.images || []).map(function (s) {
            return { src: typeof s === 'string' ? s : s.src, w: s.width, h: s.height, alt: s.alt || '' };
          });
      var fname = function (s) {
        return String(s || '').split('?')[0].split('/').pop().toLowerCase();
      };
      var pick = null;
      list.forEach(function (it) {
        var f = fname(it.src);
        if (f.indexOf('poster-only') !== -1 || f.indexOf('poster_only') !== -1 || String(it.alt).toLowerCase().indexOf('poster-only') !== -1) pick = it;
      });
      if (!pick && list.length) {
        var last = list[list.length - 1];
        var f = fname(last.src);
        var square = last.w && last.h && last.w === last.h;
        // Square posters (30x30, 50x50 ...) have a square poster-only image; only skip squares
        // that are mockups (frame/lifestyle) for portrait posters.
        var isMockup = /ramme|frame|_peleton_|mockup/.test(f);
        var squareProduct = (product.variants || []).some(function (v) { return /^\s*(\d+)\s*x\s*\1\s*$/i.test(v.option1 || v.title || ''); });
        if (!isMockup && (!square || squareProduct)) pick = last;
      }
      if (!pick || !pick.src) return null;
      var src = pick.src.indexOf('//') === 0 ? 'https:' + pick.src : pick.src;
      return { src: src, ratio: pick.w && pick.h ? pick.w / pick.h : product.aspect_ratio || 0.714, alt: pick.alt || product.title || '' };
    },

    /** True if compositing may be used for this product JSON (poster tag / exclude tags / gift card). */
    eligible: function (product) {
      if (!product || product.gift_card) return false;
      var tags = (product.tags || []).map(function (t) {
        return String(t).toLowerCase();
      });
      if (cfg.posterTag && tags.indexOf(String(cfg.posterTag).toLowerCase()) === -1) return false;
      var ex = String(cfg.excludeTags || '')
        .split(',')
        .map(function (t) {
          return t.trim().toLowerCase();
        })
        .filter(Boolean);
      for (var i = 0; i < ex.length; i++) if (tags.indexOf(ex[i]) !== -1) return false;
      return true;
    },

    /** Plain poster of the product on this page (frame popup), or null. Cached. */
    popupPoster: function () {
      if (api._popupPoster !== undefined) return api._popupPoster;
      api._popupPoster = null;
      if (!api.surfaces.popup) return null;
      var el = document.getElementById('product-json');
      if (!el) return null;
      try {
        var p = JSON.parse(el.textContent);
        if (api.eligible(p)) api._popupPoster = api.pickPoster(p);
      } catch (e) {}
      return api._popupPoster;
    },

    /** Shopify CDN url at a given width (keeps existing query params). */
    sized: function (src, width) {
      // Local images (the poster builder's own preview) can't take a ?width param.
      if (!src || /^(blob|data):/.test(src)) return src;
      try {
        var u = new URL(src, location.href);
        u.searchParams.set('width', width);
        return u.toString();
      } catch (e) {
        return src;
      }
    },

    srcset: function (src) {
      return [360, 540, 768, 1080, 1500]
        .map(function (w) {
          return api.sized(src, w) + ' ' + w + 'w';
        })
        .join(', ');
    },
  };

  window.PeletonFrames = api;

  /* ---------- Event wiring ---------- */
  document.addEventListener('peleton:frame-change', function (e) {
    var d = (e && e.detail) || {};
    var key = d.frame || api.keyForHandle(d.handle);
    if (!key || (key !== 'none' && !byKey[key])) return;
    api.current = key;
    api.setFrame(d.scope || document, key);
    syncSwatches(key);
  });

  /* ---------- Swatch row (snippets/framed-poster-swatches.liquid) ---------- */
  function syncSwatches(key) {
    [].forEach.call(document.querySelectorAll('[data-pf-swatches]'), function (row) {
      [].forEach.call(row.querySelectorAll('[data-pf-swatch]'), function (b) {
        var on = b.getAttribute('data-pf-swatch') === key;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      var cur = row.querySelector('[data-pf-current]');
      var f = byKey[key];
      if (cur && f) cur.textContent = f.label;
    });
  }

  function initSwatches(root) {
    [].forEach.call((root || document).querySelectorAll('[data-pf-swatches]:not([data-pf-ready])'), function (row) {
      row.setAttribute('data-pf-ready', '');
      // Paint the chip with the real moulding texture (top band of the overlay) over the swatch colour.
      [].forEach.call(row.querySelectorAll('[data-pf-swatch]'), function (b) {
        var f = byKey[b.getAttribute('data-pf-swatch')];
        var chip = b.querySelector('.pf-swatch__chip');
        if (f && f.overlay && chip) {
          chip.style.backgroundImage = 'url("' + f.overlay + '")';
          chip.style.backgroundSize = '3700% auto';
          chip.style.backgroundPosition = '50% 0';
        }
      });
      row.addEventListener('click', function (e) {
        var b = e.target.closest('[data-pf-swatch]');
        if (!b || row.hasAttribute('data-unframed')) return;
        api.select(b.getAttribute('data-pf-swatch'));
      });
      row.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].indexOf(e.key) === -1) return;
        var btns = [].slice.call(row.querySelectorAll('[data-pf-swatch]'));
        var i = btns.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        var n = btns[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + btns.length) % btns.length];
        n.focus();
        api.select(n.getAttribute('data-pf-swatch'));
      });
    });
    syncSwatches(api.current);
  }

  /* ---------- Unframed sizes (e.g. 100x140 delivered without frame) ---------- */
  function applyUnframed(sizeTitle) {
    var un = api.isUnframedSize(sizeTitle);
    api.unframed = un;
    [].forEach.call(document.querySelectorAll('[data-pf-switch]'), function (n) {
      if (un) n.setAttribute('data-unframed', '');
      else n.removeAttribute('data-unframed');
    });
    [].forEach.call(document.querySelectorAll('[data-pf-swatches]'), function (row) {
      if (un) row.setAttribute('data-unframed', '');
      else row.removeAttribute('data-unframed');
      var note = row.querySelector('[data-pf-unframed-note]');
      if (note) note.hidden = !un;
    });
  }

  function variantSize(v) {
    if (!v) return '';
    return v.option1 || v.title || '';
  }

  // The theme dispatches 'variant:changed' (component-variant-drawer.js, template-product.js)
  // with detail.variantTitle; 'variant:change' is kept for other emitters.
  function onVariant(e) {
    var d = (e && e.detail) || {};
    var size = d.variantTitle || variantSize(d.variant);
    if (size) applyUnframed(size);
  }
  document.addEventListener('variant:changed', onVariant);
  document.addEventListener('variant:change', onVariant);

  function init() {
    // Preload overlays on pages that can switch (cheap: 3 small PNGs), so switching is instant.
    if (document.querySelector('[data-pf-switch]')) {
      frames.forEach(function (f) {
        if (f.overlay) {
          var i = new Image();
          i.src = f.overlay;
        }
      });
    }
    initSwatches(document);
    var row = document.querySelector('[data-pf-swatches][data-initial-size]');
    if (row) applyUnframed(row.getAttribute('data-initial-size'));
  }

  /** Re-scan for swatch rows / composites added after load (AJAX, quick view). */
  api.init = function (root) {
    initSwatches(root || document);
    api.setFrame(root || document, api.current);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
